/**
 * synthesisModel — catalogue de colonnes, vues (4 onglets), rendus de cellules
 * et calculs partagés entre la page People cost synthesis et son impression.
 * Garantit des tableaux identiques entre écran et PDF.
 */
import React from 'react';
import { formatNumber } from '../../utils/format';
import { tp } from '../../i18n/useP';
import { cmpVal } from '../../utils/personTable';
import PersonName from '../../components/PersonName';

/* Catalogue complet de colonnes. Chaque vue sélectionne un sous-ensemble. */
export const COLUMNS = [
  { key: 'person_name',            label: 'Person',                 sortable: true, align: 'left',  numeric: false, personSort: true },
  { key: 'department',             label: 'Department',             sortable: true, align: 'left',  numeric: false },
  { key: 'status',                 label: 'Status',                 sortable: true, align: 'left',  numeric: false },
  { key: 'salary_base',            label: 'Base salary',            sortable: true, align: 'right', numeric: true },
  { key: 'bonus_variable',         label: 'Annual extras',          sortable: true, align: 'right', numeric: true },
  { key: 'effective_remuneration', label: 'Gross Salary',           sortable: true, align: 'right', numeric: true },
  { key: 'cs_empr_rate_pct',       label: 'SC Empr. %',             sortable: true, align: 'right', numeric: true },
  { key: 'lpp_empr_pct',           label: 'LPP Empr. %',            sortable: true, align: 'right', numeric: true },
  { key: 'total_empr_pct',         label: 'Total Empr. %',          sortable: true, align: 'right', numeric: true },
  { key: 'social_charges_empr',    label: 'SC Empr.',               sortable: true, align: 'right', numeric: true },
  { key: 'cs_empl_rate_pct',       label: 'SC Empl. %',             sortable: true, align: 'right', numeric: true },
  { key: 'lpp_empl_pct',           label: 'LPP Empl. %',            sortable: true, align: 'right', numeric: true },
  { key: 'total_empl_pct',         label: 'Total Empl. %',          sortable: true, align: 'right', numeric: true },
  { key: 'social_charges_empl',    label: 'SC Empl.',               sortable: true, align: 'right', numeric: true },
  { key: 'loaded_payroll',         label: 'Loaded payroll',         sortable: true, align: 'right', numeric: true },
  { key: 'fixed_indemnities',      label: 'Fixed indem.',           sortable: true, align: 'right', numeric: true },
  { key: 'paid_indemnities',       label: 'Paid indem.',            sortable: true, align: 'right', numeric: true },
  { key: 'other_expenses',         label: 'Other exp.',             sortable: true, align: 'right', numeric: true },
  { key: 'other_fees',             label: 'Total expenses',         sortable: true, align: 'right', numeric: true },
  { key: 'total_cost_empr',        label: 'Total personnel cost',   sortable: true, align: 'right', numeric: true },
  { key: 'estimated_net',          label: 'Net paid',               sortable: true, align: 'right', numeric: true },
  { key: 'received_indemnities',   label: 'Received indem.',        sortable: true, align: 'right', numeric: true },
];

/* 4 vues — chaque tableau filtre les colonnes selon le périmètre métier. */
export const VIEWS = {
  personnel_cost: {
    label: 'Personnel cost',
    cols: ['person_name', 'department', 'status',
           'salary_base', 'bonus_variable', 'effective_remuneration',
           'social_charges_empr', 'loaded_payroll', 'other_fees', 'total_cost_empr'],
  },
  expenses: {
    label: 'Expenses',
    cols: ['person_name', 'department',
           'other_fees', 'received_indemnities'],
  },
  net_paid: {
    label: 'Net paid',
    cols: ['person_name', 'department',
           'effective_remuneration', 'social_charges_empl', 'other_fees', 'estimated_net'],
  },
  social_charges: {
    label: 'Social charges',
    cols: ['person_name', 'department', 'effective_remuneration',
           'cs_empr_rate_pct', 'lpp_empr_pct', 'total_empr_pct', 'social_charges_empr',
           'cs_empl_rate_pct', 'lpp_empl_pct', 'total_empl_pct', 'social_charges_empl'],
  },
};

export const VIEW_ORDER = ['personnel_cost', 'expenses', 'net_paid', 'social_charges'];

// Comparateur partagé (nulls last, numeric-first) — source unique utils/personTable.
export { cmpVal };

const STATUS_LABEL = {
  active:      { label: 'active',  cls: 'hr-status--active'   },
  joined:      { label: 'joined',  cls: 'hr-status--joined'   },
  left:        { label: 'left',    cls: 'hr-status--left'     },
  left_before: { label: 'past',    cls: 'hr-status--inactive' },
  inactive:    { label: 'inactive',cls: 'hr-status--inactive' },
};

/* Rendu d'une cellule selon la colonne. */
export function renderCell(col, r) {
  const k = col.key;
  if (k === 'person_name')  return <td><PersonName firstName={r.first_name} lastName={r.last_name} /></td>;
  if (k === 'department')   return <td className={r.department ? '' : 'hr-page__muted'}>{r.department || '—'}</td>;
  if (k === 'status') {
    const s = STATUS_LABEL[r.status] || STATUS_LABEL.inactive;
    return <td><span className={`hr-status ${s.cls}`}>{s.label}</span></td>;
  }
  if (k === 'cs_empr_rate_pct') return <td className="hr-page__num">{formatNumber(r.cs_empr_rate_pct, 2)}%</td>;
  if (k === 'lpp_empr_pct') {
    return (
      <td className="hr-page__num"
          style={r.lpp_from_fallback ? { background: 'var(--terracotta-light)', color: 'var(--terracotta-dark)', fontStyle: 'italic' } : undefined}
          title={r.lpp_from_fallback ? 'Aucun taux LPP saisi pour cette personne — taux par défaut tenant appliqué. À renseigner dans hr_lpp_rates.' : 'LPP empr CHF / remuneration'}>
        {formatNumber(r.lpp_empr_pct, 2)}%{r.lpp_from_fallback && ' ~'}
      </td>
    );
  }
  if (k === 'total_empr_pct') return <td className="hr-page__num"><strong>{formatNumber(r.total_empr_pct, 2)}%</strong></td>;
  if (k === 'cs_empl_rate_pct') return <td className="hr-page__num">{formatNumber(r.cs_empl_rate_pct, 2)}%</td>;
  if (k === 'lpp_empl_pct') return <td className="hr-page__num">{formatNumber(r.lpp_empl_pct, 2)}%</td>;
  if (k === 'total_empl_pct') return <td className="hr-page__num"><strong>{formatNumber(r.total_empl_pct, 2)}%</strong></td>;
  if (k === 'loaded_payroll') return <td className="hr-page__num"><strong>{formatNumber(r.loaded_payroll, 0)}</strong></td>;
  if (k === 'total_cost_empr') return <td className="hr-page__num"><strong>{formatNumber(r.total_cost_empr, 0)}</strong></td>;
  if (k === 'received_indemnities') {
    return (
      <td className={`hr-page__num ${Number(r.received_indemnities) ? '' : 'hr-page__zero'}`}
          style={Number(r.received_indemnities) ? { color: 'var(--sage-dark)' } : undefined}
          title={tp('phrases:p_6591b0ef8c')}>
        {formatNumber(r.received_indemnities, 0)}
      </td>
    );
  }
  if (k === 'bonus_variable' || k === 'other_fees' || k === 'fixed_indemnities' || k === 'paid_indemnities' || k === 'other_expenses') {
    return <td className={`hr-page__num ${Number(r[k]) ? '' : 'hr-page__zero'}`}>{formatNumber(r[k], 0)}</td>;
  }
  return <td className="hr-page__num">{formatNumber(r[k], 0)}</td>;
}

/* Rendu d'une cellule du tfoot (totaux). */
export function renderTotalCell(col, totals) {
  const k = col.key;
  if (k === 'person_name') return <td><strong>Total / Avg</strong></td>;
  if (k === 'department')  return <td className="hr-page__muted">{totals.count} {totals.count > 1 ? 'persons' : 'person'}</td>;
  if (k === 'status')      return <td></td>;
  if (k === 'cs_empr_rate_pct') return <td className="hr-page__num"><strong>{formatNumber(totals.avg_cs_empr_rate_pct, 2)}%</strong></td>;
  if (k === 'lpp_empr_pct')     return <td className="hr-page__num"><strong>{formatNumber(totals.avg_lpp_empr_pct, 2)}%</strong></td>;
  if (k === 'total_empr_pct')   return <td className="hr-page__num"><strong>{formatNumber(totals.avg_total_empr_pct, 2)}%</strong></td>;
  if (k === 'cs_empl_rate_pct') return <td className="hr-page__num"><strong>{formatNumber(totals.avg_cs_empl_rate_pct, 2)}%</strong></td>;
  if (k === 'lpp_empl_pct')     return <td className="hr-page__num"><strong>{formatNumber(totals.avg_lpp_empl_pct, 2)}%</strong></td>;
  if (k === 'total_empl_pct')   return <td className="hr-page__num"><strong>{formatNumber(totals.avg_total_empl_pct, 2)}%</strong></td>;
  if (k === 'received_indemnities') return <td className="hr-page__num" style={{ color: 'var(--sage-dark)' }}><strong>{formatNumber(totals.total_received_indemnities, 0)}</strong></td>;
  const totalKeyMap = {
    salary_base:            'total_salary_base',
    bonus_variable:         'total_bonus_variable',
    effective_remuneration: 'total_effective_remuneration',
    social_charges_empr:    'total_social_empr',
    social_charges_empl:    'total_social_empl',
    loaded_payroll:         'total_loaded_payroll',
    fixed_indemnities:      'total_fixed_indemnities',
    paid_indemnities:       'total_paid_indemnities',
    other_expenses:         'total_other_expenses',
    other_fees:             'total_other_fees',
    total_cost_empr:        'total_cost_empr',
    estimated_net:          'total_estimated_net',
  };
  const tk = totalKeyMap[k];
  const v = tk ? totals[tk] : 0;
  return <td className="hr-page__num"><strong>{formatNumber(v, 0)}</strong></td>;
}

/* Enrichit les lignes brutes de l'API (person_name, total_empr_pct, sort keys). */
export function enrichRows(data) {
  const rows = data?.rows || [];
  return rows.map(r => ({
    ...r,
    person_name: `${r.last_name} ${r.first_name}`,
    first_name_sort: (r.first_name || '').toLowerCase(),
    last_name_sort:  (r.last_name  || '').toLowerCase(),
    total_empr_pct: Number(r.cs_empr_rate_pct || 0) + Number(r.lpp_empr_pct || 0),
  }));
}

/* Totaux + moyennes pondérées par la rémunération effective. */
export function computeTotals(rows) {
  const sum = (k) => rows.reduce((a, r) => a + Number(r[k] || 0), 0);
  const total_effective_remuneration = sum('effective_remuneration');
  const weightedAvg = (k) => {
    if (total_effective_remuneration <= 0) return 0;
    const num = rows.reduce((a, r) => a + Number(r[k] || 0) * Number(r.effective_remuneration || 0), 0);
    return num / total_effective_remuneration;
  };
  return {
    count:                          rows.length,
    total_salary_base:              sum('salary_base'),
    total_bonus_variable:           sum('bonus_variable'),
    total_effective_remuneration,
    total_social_empr:              sum('social_charges_empr'),
    total_social_empl:              sum('social_charges_empl'),
    total_loaded_payroll:           sum('loaded_payroll'),
    total_fixed_indemnities:        sum('fixed_indemnities'),
    total_paid_indemnities:         sum('paid_indemnities'),
    total_other_expenses:           sum('other_expenses'),
    total_other_fees:               sum('other_fees'),
    total_received_indemnities:     sum('received_indemnities'),
    total_cost_empr:                sum('total_cost_empr'),
    total_estimated_net:            sum('estimated_net'),
    avg_cs_empr_rate_pct:           weightedAvg('cs_empr_rate_pct'),
    avg_lpp_empr_pct:               weightedAvg('lpp_empr_pct'),
    avg_total_empr_pct:             weightedAvg('total_empr_pct'),
    avg_cs_empl_rate_pct:           weightedAvg('cs_empl_rate_pct'),
    avg_lpp_empl_pct:               weightedAvg('lpp_empl_pct'),
    avg_total_empl_pct:             weightedAvg('total_empl_pct'),
  };
}
